/**
 * COMMERCIAL CAPACITY → HEBREW. OA-0001 §3.11, 08 §7.
 *
 *   > At the limit, the product directs the customer into the appropriate commercial upgrade flow …
 *   > Commercial enforcement must not corrupt or delete existing Company data.
 *
 * ── WHAT THIS FILE MAY AND MAY NOT DO ───────────────────────────────────────────────────────────
 *
 * It may SUBTRACT and it may WRITE A SENTENCE. It may not DECIDE.
 *
 * The decision — "may another one be created" — lives in `apps/api/src/domain/capacity.ts` and
 * nowhere else, and it arrives here already made, as a `CapacityRefusal` carrying the count, the
 * limit and the upgrade address. A screen that inferred "you are at your limit" from two numbers
 * would be a second implementation of a commercial rule, which is family F9 with a Hebrew accent:
 * two answers to one question, drifting apart at their own pace.
 *
 * `remainingOf` therefore exists to LABEL a state the server already described, and the refusal
 * screen never consults it — it renders the refusal it was handed.
 *
 * ── WHY THE REFUSAL IS NEVER A GENERIC ERROR ────────────────────────────────────────────────────
 *
 * 08 §7 is explicit that a hard block returns "a SPECIFIC ACTIONABLE reason with the count, the
 * limit and an upgrade path — never a generic 403, never a silent failure". So the refusal text
 * below always contains three facts and one route, and the test asserts all four are present. An
 * office that is told only "לא ניתן להוסיף חברה" has been told to phone somebody.
 *
 * ── AND WHY OVER-CAPACITY IS A STATE, NOT AN IMPOSSIBILITY ──────────────────────────────────────
 *
 * A plan change can lower a limit below current usage. Existing rows are ALWAYS kept — capacity
 * refuses creation and never degrades what exists — so the product must be able to SAY "you are
 * three over" rather than treating it as a corrupt state it has no words for.
 */

import type { CapacityKind, CapacityLimit, CapacityRefusal, CapacityUsage } from './contract';
import { capacityKindHe } from './adminVocabulary';

/**
 * How many more are within the purchased limit. `null` ⇒ unmetered, which is a SHAPE and not an
 * absent number: an adapter that could not read the limit cannot spell "unlimited" by accident.
 *
 * Presentation only. Nothing branches on this to permit or refuse anything.
 */
export function remainingOf(usage: CapacityUsage): number | null {
  if (!usage.limit.metered) return null;
  return Math.max(0, usage.limit.limit - usage.current);
}

/** How far over a lowered limit the office already is. Changes no permission; every row keeps working. */
export function overCapacityBy(usage: CapacityUsage): number {
  if (!usage.limit.metered) return 0;
  return Math.max(0, usage.current - usage.limit.limit);
}

export function limitHe(limit: CapacityLimit): string {
  return limit.metered ? String(limit.limit) : 'ללא הגבלה';
}

export interface CapacityLineText {
  readonly titleHe: string;
  /** "נרכשו 25 · בשימוש 18 · פנויים 7" — the three numbers, always all three. */
  readonly summaryHe: string;
  /** Present only when the office is over a lowered limit. Existing rows keep working regardless. */
  readonly overCapacityHe: string | null;
  readonly tone: 'ok' | 'warn' | 'error' | 'muted';
}

/**
 * One line of the capacity view — purchased, used, remaining, plainly, in that order.
 *
 * "Plainly" is a requirement, not a style note: 09 §5.1's "313 is not 313" rule says any figure of
 * the form "N חברות" must state WHICH N it is, and a bare "18 חברות" on a subscription screen is
 * exactly the number a customer cannot reconcile against an invoice.
 */
export function capacityLineText(usage: CapacityUsage): CapacityLineText {
  const noun = capacityKindHe(usage.kind);
  const over = overCapacityBy(usage);
  const remaining = remainingOf(usage);

  if (!usage.limit.metered) {
    return {
      titleHe: noun.nounHe,
      summaryHe: `נרכשו ללא הגבלה · בשימוש ${usage.current}`,
      overCapacityHe: null,
      tone: 'ok',
    };
  }

  const summaryHe =
    `נרכשו ${usage.limit.limit} ${noun.unitHe} · ` +
    `בשימוש ${usage.current} · ` +
    `פנויים ${remaining ?? 0}`;

  if (over > 0) {
    return {
      titleHe: noun.nounHe,
      summaryHe,
      // The sentence says the two things that are both true, because only saying the first is what
      // makes a customer expect a deletion that is never going to happen.
      overCapacityHe: `הארגון נמצא ${over} מעל המכסה שנרכשה. כל מה שקיים ממשיך לעבוד, ולא ניתן להוסיף חדשים.`,
      tone: 'error',
    };
  }

  return {
    titleHe: noun.nounHe,
    summaryHe,
    overCapacityHe: null,
    tone: remaining === 0 ? 'warn' : 'ok',
  };
}

export interface CapacityRefusalText {
  readonly titleHe: string;
  /** Carries the count AND the limit. Both, in words, every time. */
  readonly explanationHe: string;
  /** The written next step. The route itself travels beside it as `upgradeHref`. */
  readonly nextStepHe: string;
  readonly upgradeHref: string;
}

const REFUSAL_TITLE_HE: Readonly<Record<CapacityKind, string>> = {
  company: 'נוצלה מלוא מכסת החברות במנוי',
  seat: 'נוצלה מלוא מכסת המשתמשים במנוי',
  module_company: 'נוצלה מלוא מכסת החברות במודול',
};

/**
 * The refusal, rendered. Three facts and one route — 08 §7, and nothing less counts.
 *
 * The action word is deliberately "הרחבת המכסה" and not a price: O-3 forbids naming a price or
 * putting a purchase button inside a refusal, because the person who hits the limit is very often
 * not the person who may spend the money.
 */
export function capacityRefusalText(refusal: CapacityRefusal): CapacityRefusalText {
  const noun = capacityKindHe(refusal.kind);
  return {
    titleHe: REFUSAL_TITLE_HE[refusal.kind],
    explanationHe:
      // Gender-neutral by construction: the noun that follows changes gender with the capacity kind
      // (חברות is feminine, מושבים is masculine), and a sentence that agreed with only one of them
      // would read as broken Hebrew on the other screen.
      `במנוי של הארגון נרכשו ${refusal.limit} ${noun.unitHe}, והמכסה מנוצלת במלואה (${refusal.current} מתוך ${refusal.limit}). ` +
      'כל מה שקיים ממשיך לעבוד כרגיל — ההגבלה חלה על הוספה בלבד.',
    nextStepHe: 'מעבר למסך המנוי להרחבת המכסה',
    upgradeHref: refusal.upgradeHref,
  };
}
